"use client"
import { useState, useEffect, useCallback, useMemo } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import Skeleton from "react-loading-skeleton"
import axios from "axios"
import {
  updateTicket,
  searchTicketActivity,
  getTicketByIdPublic,
  getTicketsByAgent,
  getTicketHistory,
  getInquiryByClientId,
  updateInquiry,
} from "../../../services/AgentServices"
import {
  FaArrowLeft,
  FaClipboardList,
  FaComment,
  FaCalendarAlt,
  FaExclamationTriangle,
  FaUser,
  FaPhone,
  FaTools,
  FaCog,
  FaInfoCircle,
  FaCheckCircle,
} from "react-icons/fa"
import { Card, Form, Button, Badge, Alert, Row, Col, Container, Toast } from "react-bootstrap"
import CryptoJS from "crypto-js"

const SECRET_KEY = process.env.REACT_APP_ID_SECRET
const BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://api.ataichatbot.mcndhanore.co.in"



// Storage keys for event-driven updates
const STORAGE_KEYS = {
  TICKET_UPDATE: "ticket_updated",
  DATA_CHANGED: "tickets_data_changed",
}

const UserConversation = () => {
  const [tickets, setTickets] = useState([])
  const [inquiry, setInquiry] = useState(null)
  const [agentRemarks, setAgentRemarks] = useState("")
  const [followUpDate, setFollowUpDate] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [user, setUser] = useState(null)
  const navigate = useNavigate()
  const [remarkError, setRemarkError] = useState("")
  const [statusError, setStatusError] = useState("")
  const [followUpError, setFollowUpError] = useState("")
  const [originalStatusCode, setOriginalStatusCode] = useState(null)
  const [filterStatus, setFilterStatus] = useState("All")
  const [lastRemarkInput, setLastRemarkInput] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [conversationDuration, setConversationDuration] = useState(null)
  const [activityRecord, setActivityRecord] = useState(null)
  const [ticketRemarks, setTicketRemarks] = useState([])

  // Toast notification states
  const [showRemarkToast, setShowRemarkToast] = useState(false)
  const [showStatusToast, setShowStatusToast] = useState(false)
  const [showFollowUpToast, setShowFollowUpToast] = useState(false)
  const [showSuccessToast, setShowSuccessToast] = useState(false)
  const [toastMessage, setToastMessage] = useState("")

  const queryParams = new URLSearchParams(useLocation().search)
  const token = queryParams.get("token")
  const type = queryParams.get("type") || "ticket"
  const ticketIdParam = queryParams.get(type === 'inquiry' ? 'inquiryId' : 'ticketId')
  const ticketId = ticketIdParam ? Number.parseInt(ticketIdParam, 10) : null
  const location = useLocation()
  const statusFromLink = location.state?.status?.toUpperCase()
  const [complaints, setComplaints] = useState([])

  // Memoized constants
  const clientId = useMemo(() => sessionStorage.getItem("clientId") || "", [])
  const user_id = useMemo(() => sessionStorage.getItem("userId") || "", [])
  const agentId = useMemo(() => Number.parseInt(user_id), [user_id])

  // Helper function to deduplicate complaints - memoized
  const deduplicateComplaints = useCallback((complaints) => {
    if (!Array.isArray(complaints)) return []
    const uniqueComplaints = []
    const seen = new Set()
    complaints.forEach((complaint) => {
      const key = `${complaint.complaint_detail || ""}-${complaint.complaintID || ""}`
      if (!seen.has(key)) {
        seen.add(key)
        uniqueComplaints.push(complaint)
      }
    })
    return uniqueComplaints
  }, [])

  const getUniqueProductNames = useCallback((complaints) => {
    if (!Array.isArray(complaints)) return []
    const productNames = complaints
      .map((c) => c.product_name)
      .filter((name) => name && name.trim() !== "" && name.toLowerCase() !== "null")
    return [...new Set(productNames)]
  }, [])

  // decrypt it to get the real userId - memoized
  const decryptedUserId = useMemo(() => {
    try {
      const bytes = CryptoJS.AES.decrypt(decodeURIComponent(token || ""), SECRET_KEY)
      return bytes.toString(CryptoJS.enc.Utf8)
    } catch {
      console.error("Invalid or missing token")
      return null
    }
  }, [token])

  // Status options with flexible workflow - memoized
  const statusOptions = useMemo(
    () => [
      {
        code: "INP",
        label: "In Progress, communication is going on with client.",
        color: "warning",
        bgColor: "#fff3cd",
        textColor: "#856404",
        requiresFollowUp: true, // Follow-up date is mandatory for INP
      },
      {
        code: "CRS",
        label: "Resolved",
        color: "success",
        bgColor: "#d1edff",
        textColor: "#0c5460",
        requiresFollowUp: false, // Follow-up date is disabled for CRS
        disableFollowUp: true,
      },
      {
        code: "CNR",
        label: "No Response from Client so Closed",
        color: "danger",
        bgColor: "#f8d7da",
        textColor: "#721c24",
        requiresFollowUp: false, // Follow-up date is disabled for CNR
        disableFollowUp: true,
      },
    ],
    [],
  )

  const [status, setStatus] = useState("")
  const [currentTicketStatus, setCurrentTicketStatus] = useState("") // Track current ticket status
  const [ticketDetail, setTicketDetail] = useState(null)

  const fetchTicketHistory = useCallback(async () => {
    if (!ticketId || !clientId) return

    try {
      const historyData = await getTicketHistory(ticketId, clientId)
      if (historyData.status === "success" && Array.isArray(historyData.data)) {
        const remarks = historyData.data
          .filter(
            (item) =>
              (item.description && item.description.trim() !== "") || (item.remarks && item.remarks.trim() !== ""),
          )
          .map((item) => ({
            remark: item.description || item.remarks, // Use description as primary, fallback to remarks
            createdAt: item.created_at || item.createdAt,
            createdBy: item.created_by || item.createdBy,
          }))
        setTicketRemarks(remarks)
      }
    } catch (error) {
      console.error("Error fetching ticket history:", error)
    }
  }, [ticketId, clientId])

  useEffect(() => {
    const fetchTicket = async () => {
      const data = await getTicketByIdPublic(ticketId)
      setTicketDetail(data)
    }

    fetchTicket()
    fetchTicketHistory()
  }, [fetchTicketHistory])

  // Function to trigger data refresh in tickets list
  const triggerTicketsRefresh = useCallback(() => {
    // Set storage event to trigger refresh in tickets list
    localStorage.setItem(STORAGE_KEYS.TICKET_UPDATE, Date.now().toString())
    localStorage.setItem(STORAGE_KEYS.DATA_CHANGED, Date.now().toString())

    // Also call global refresh function if available
    if (window.refreshTicketsData) {
      window.refreshTicketsData()
    }
  }, [])

  // Optimized data loading with parallel API calls
  const loadAllData = useCallback(async () => {
    setLoading(true)
    try {
      if (type === "inquiry") {
        if (!user_id || !clientId || !ticketId) return;
        const inquiryPromise = await getInquiryByClientId(clientId, 1, 0);
        if (inquiryPromise && inquiryPromise.success && Array.isArray(inquiryPromise.data)) {
           const inq = inquiryPromise.data.find(i => Number.parseInt(i.id) === ticketId);
           if (inq) {
             const complaintsMapped = inq.last_question ? [{
                id: inq.last_question.id,
                complaint_detail: inq.last_question.text,
                followup_date: inq.Next_followup
             }] : [];
             setComplaints(complaintsMapped);
             setTicketDetail({
               device_id: null,
               product_name: "Inquiry",
               complaint_detail: inq.last_question?.text || "",
               complaint_mobile: inq.contact,
               description: inq.agent_remarks,
               complaintname: inq.Client_name,
               complaints: complaintsMapped,
             });
             setCurrentTicketStatus(inq.status_code || "OPN");
             setActivityRecord({
                status: inq.status_code || "OPN",
                nextFollowUp: inq.Next_followup,
                Description: inq.agent_remarks
             });
             setTicketRemarks([]);
           }
        }
      } else {
      const [publicTicketPromise, agentTicketsPromise, activityPromise, historyPromise] = await Promise.allSettled([
        getTicketByIdPublic(ticketId),
        clientId && agentId && ticketId ? getTicketsByAgent(clientId, agentId) : Promise.resolve(null),
        user_id ? searchTicketActivity(user_id) : Promise.resolve(null),
        ticketId && clientId ? getTicketHistory(ticketId, clientId) : Promise.resolve(null),
      ])

      // Process public ticket data
      if (publicTicketPromise.status === "fulfilled" && publicTicketPromise.value) {
        const publicTicket = publicTicketPromise.value
        const deduplicatedComplaints = deduplicateComplaints(publicTicket.complaints || [])
        setComplaints(deduplicatedComplaints)
        setTicketDetail({
          device_id: publicTicket.deviceID,
          product_name: publicTicket.product_name,
          complaint_detail: publicTicket.complaint_detail,
          complaint_mobile: publicTicket.complaint_mobile,
          description: publicTicket.description,
          complaintname: publicTicket.complaintname,
          complaints: deduplicatedComplaints,
        })
      }

      // Process agent tickets data
      if (
        agentTicketsPromise.status === "fulfilled" &&
        agentTicketsPromise.value?.success &&
        Array.isArray(agentTicketsPromise.value.tickets)
      ) {
        const ticket = agentTicketsPromise.value.tickets.find((t) => t.ticket_id === ticketId)
        if (ticket) {
          setCurrentTicketStatus(ticket.status || "OPN")
          setActivityRecord({
            id: ticket.activity_id ?? null,
            ticketId: ticket.ticket_id ?? null,
            status: ticket.status ?? "OPN",
            nextFollowUp: ticket.next_followup ?? null,
            description: ticket.complaint_description ?? "",
            createdAt: ticket.created_at ?? null,
            updatedAt: ticket.updated_at ?? null,
            assignedTo: ticket.assigned_to ?? null,
            createdBy: ticket.created_by ?? null,
          })
        }
      }

      // Process activity data
      if (
        activityPromise.status === "fulfilled" &&
        activityPromise.value?.status &&
        Array.isArray(activityPromise.value.data)
      ) {
        const record = activityPromise.value.data.find(
          (rec) => Number.parseInt(rec.ticket_id || rec.ticketId) === Number.parseInt(ticketId),
        )
        if (record) {
          setActivityRecord((prev) => ({
            ...prev,
            ...record,
          }))
        }
      }

      if (historyPromise.status === "fulfilled" && historyPromise.value?.status === "success") {
        const remarks = historyPromise.value.data
          .filter(
            (item) =>
              (item.description && item.description.trim() !== "") || (item.remarks && item.remarks.trim() !== ""),
          )
          .map((item) => ({
            remark: item.description || item.remarks, // Use description as primary, fallback to remarks
            createdAt: item.created_at || item.createdAt,
            createdBy: item.created_by || item.createdBy,
          }))
        setTicketRemarks(remarks)
      }
      } // end type === ticket else block
    } catch (err) {
      console.error("Error loading data:", err)
      setError(`Failed to load ${type} activity data.`)
    } finally {
      setLoading(false)
    }
  }, [ticketId, clientId, agentId, user_id, type, deduplicateComplaints])

  useEffect(() => {
    if (ticketId) {
      loadAllData()
    }
  }, [ticketId, loadAllData])

  const canCloseTicket = useCallback(() => {
    if (!inquiry?.Next_followup) return true
    const followUpDate = new Date(inquiry.Next_followup)
    const now = new Date()
    followUpDate.setHours(0, 0, 0, 0)
    now.setHours(0, 0, 0, 0)
    return now >= followUpDate
  }, [inquiry])

  // Optimized useEffect for activity record processing
  useEffect(() => {
    if (!activityRecord) return

    let code = activityRecord.status
    if (!statusOptions.some((opt) => opt.code === code)) {
      const found = statusOptions.find((opt) => opt.label === activityRecord.status)
      if (found) code = found.code
    }

    setCurrentTicketStatus(code || "OPN")
    setAgentRemarks(activityRecord.Description || "")

    if (activityRecord?.nextFollowUp) {
      const dateStr = formatDateToInput(activityRecord.nextFollowUp)
      setFollowUpDate(dateStr)
    } else {
      setFollowUpDate("")
    }
  }, [activityRecord, statusOptions])

  const allFieldsDisabled = useMemo(() => ["CRS", "CNR"].includes(statusFromLink), [statusFromLink])

  const isFollowUpLocked = useCallback(() => {
    if (!inquiry?.Next_followup) return false
    const followUpDate = new Date(inquiry.Next_followup)
    const now = new Date()
    followUpDate.setHours(0, 0, 0, 0)
    now.setHours(0, 0, 0, 0)
    return now < followUpDate
  }, [inquiry])

  const isTicketClosed = useCallback(() => status === "CRS" || status === "CNR", [status])

  const isOriginallyClosed = useCallback(() => ["CRS", "CNR"].includes(statusFromLink), [statusFromLink])

  const handleDateClick = useCallback((date) => {
    setFollowUpDate(date)
    console.log("Follow-up date selected:", date)
  }, [])

  // Check if follow-up date should be disabled based on selected status
  const isFollowUpDisabled = useCallback(() => {
    const selectedOption = statusOptions.find((opt) => opt.code === status)
    return selectedOption?.disableFollowUp || false
  }, [status, statusOptions])

  // Check if follow-up date is required based on selected status
  const isFollowUpRequired = useCallback(() => {
    const selectedOption = statusOptions.find((opt) => opt.code === status)
    return selectedOption?.requiresFollowUp || false
  }, [status, statusOptions])

  const assignedTo = useMemo(() => Number.parseInt(user_id), [user_id])

  // Function to get allowed status options based on current status - UPDATED
  const getAllowedStatusOptions = useCallback(() => {
    const current = currentTicketStatus || "OPN"
    console.log("Current ticket status:", current)

    if (current === "OPN") {
      // From OPN, can go to ANY status (INP, CRS, CNR)
      return statusOptions
    } else if (current === "INP") {
      // From INP, can go to INP (to update follow-up), CRS, or CNR
      return statusOptions // Allow all options including staying in INP
    } else {
      // If already closed (CRS/CNR), no status changes allowed
      return []
    }
  }, [currentTicketStatus, statusOptions])

  // Check if current ticket is in closed status
  const isCurrentTicketClosed = useCallback(() => {
    const current = currentTicketStatus || "OPN"
    return current === "CRS" || current === "CNR"
  }, [currentTicketStatus])

  // Function to show toast notifications
  const showToast = useCallback((type, message) => {
    setToastMessage(message)
    if (type === "remark") {
      setShowRemarkToast(true)
      setTimeout(() => setShowRemarkToast(false), 5000)
    } else if (type === "status") {
      setShowStatusToast(true)
      setTimeout(() => setShowStatusToast(false), 5000)
    } else if (type === "followup") {
      setShowFollowUpToast(true)
      setTimeout(() => setShowFollowUpToast(false), 5000)
    } else if (type === "success") {
      setShowSuccessToast(true)
      setTimeout(() => setShowSuccessToast(false), 3000)
    }
  }, [])

  const handleStatusChange = useCallback(
    (selectedStatus) => {
      setStatus(selectedStatus)
      console.log("Status changed to:", selectedStatus)

      // Clear follow-up date if CRS or CNR is selected
      if (selectedStatus === "CRS" || selectedStatus === "CNR") {
        setFollowUpDate("")
        setFollowUpError("")
      }

      // Clear errors when user selects status
      if (statusError) {
        setStatusError("")
        setShowStatusToast(false)
      }
    },
    [statusError, setShowStatusToast],
  )

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault()

      // Clear previous errors and toasts
      setError("")
      setRemarkError("")
      setStatusError("")
      setFollowUpError("")
      setShowRemarkToast(false)
      setShowStatusToast(false)
      setShowFollowUpToast(false)

      let valid = true
      let hasRemarkError = false
      let hasStatusError = false
      let hasFollowUpError = false

      console.log("🔍 Form Validation Check:")
      console.log("Current remark:", agentRemarks)
      console.log("Selected status:", status)
      console.log("Current ticket status:", currentTicketStatus)
      console.log("Follow-up date:", followUpDate)
      console.log("Is follow-up required:", isFollowUpRequired())

      // Validate remark field
      if (!agentRemarks || !agentRemarks.trim()) {
        const remarkErrorMsg = "Remark is required to update the ticket."
        setRemarkError(remarkErrorMsg)
        showToast(
          "remark",
          "Please enter a remark before updating the ticket. This field is mandatory for ticket updates.",
        )
        hasRemarkError = true
        valid = false
        console.log("❌ Remark validation failed")
      }

      // Validate status field - CRITICAL CHECK
      if (!status || status === "" || status.trim() === "") {
        const statusErrorMsg = "Status selection is required."
        setStatusError(statusErrorMsg)
        showToast(
          "status",
          "Please select a ticket status before updating. You must choose the appropriate status for this ticket.",
        )
        hasStatusError = true
        valid = false
        console.log("❌ Status validation failed - Status is empty or not selected")
      }

      // Validate follow-up date for INP status
      if (status === "INP" && (!followUpDate || followUpDate.trim() === "")) {
        const followUpErrorMsg = "Follow-up date is required for In Progress status."
        setFollowUpError(followUpErrorMsg)
        showToast(
          "followup",
          "Follow-up date is mandatory when setting status to 'In Progress'. Please select a follow-up date.",
        )
        hasFollowUpError = true
        valid = false
        console.log("❌ Follow-up date validation failed for INP status")
      }

      // Log validation results
      console.log("📋 Validation Summary:")
      console.log("Has remark error:", hasRemarkError)
      console.log("Has status error:", hasStatusError)
      console.log("Has follow-up error:", hasFollowUpError)
      console.log("Form is valid:", valid)

      // ABSOLUTELY DO NOT PROCEED if validation fails
      if (!valid) {
        console.log("🚫 FORM SUBMISSION BLOCKED - Validation failed")

        // Focus on the first invalid field
        if (hasRemarkError) {
          setTimeout(() => {
            const remarkField = document.querySelector('textarea[placeholder*="remark"]')
            if (remarkField) remarkField.focus()
          }, 100)
        } else if (hasStatusError) {
          setTimeout(() => {
            const statusField = document.querySelector("select")
            if (statusField) statusField.focus()
          }, 100)
        } else if (hasFollowUpError) {
          setTimeout(() => {
            const followUpField = document.querySelector('input[type="date"]')
            if (followUpField) followUpField.focus()
          }, 100)
        }

        return // STOP EXECUTION HERE - DO NOT PROCEED WITH API CALL
      }

      // Double-check before API call
      if (!status || status === "") {
        console.log("🚫 FINAL CHECK FAILED - Status still empty, aborting API call")
        setStatusError("Status must be selected before updating ticket.")
        showToast("status", "Critical Error: Status selection is mandatory. Please select a status to proceed.")
        return
      }

      // Final check for INP status follow-up requirement
      if (status === "INP" && (!followUpDate || followUpDate.trim() === "")) {
        console.log("🚫 FINAL CHECK FAILED - INP status requires follow-up date")
        setFollowUpError("Follow-up date is required for In Progress status.")
        showToast("followup", "Critical Error: Follow-up date is mandatory for In Progress status.")
        return
      }

      console.log("✅ All validations passed, proceeding with API call")

      const safeComplaints = Array.isArray(complaints) ? complaints : []

      setLoading(true)

      try {
        if (type === "inquiry") {
          const payload = {
            p_id: ticketId,
            p_idstatus: status,
            p_idagent_remarks: agentRemarks,
            p_idNext_followup: followUpDate || new Date().toISOString().split('T')[0],
            user_id: Number.parseInt(user_id),
            isFinalStatus: false
          }

          console.log("📦 Final Payload to updateInquiry:", payload)
          const result = await updateInquiry(payload)
          console.log("✅ Update successful:", result)

          triggerTicketsRefresh()
          showToast("success", "Inquiry status updated successfully! Redirecting...")
          setTimeout(() => navigate("/agent/components/Inquiries", { replace: true }), 2000)
        } else {
          // Build payload according to your API structure
          const payload = {
            ticketId: ticketId,
            description: agentRemarks,
            updatedBy: Number.parseInt(user_id),
            complaintIDs: safeComplaints.map((comp) => ({
              p_DC_id: comp.id || comp.complaintID,
              description: agentRemarks,
              followUpDate: followUpDate ? formatDateToInput(followUpDate) : "",
              assignedTo: Number.parseInt(user_id),
              status: status,
            })),
          }

          console.log("📦 Final Payload to updateTicket:", payload)
          const result = await updateTicket(payload)
          console.log("✅ Update successful:", result)

          triggerTicketsRefresh()
          showToast("success", "Ticket status updated successfully! Redirecting to tickets list...")
          setTimeout(() => navigate("/agent/components/Tickets", { replace: true }), 2000)
        }
      } catch (err) {
        console.error("❌ Update error:", err)
        setError(`Failed to update ${type}. Please try again.`)
      } finally {
        setLoading(false)
      }
    },
    [
      agentRemarks,
      status,
      currentTicketStatus,
      followUpDate,
      isFollowUpRequired,
      showToast,
      complaints,
      ticketId,
      user_id,
      navigate,
      triggerTicketsRefresh,
      type
    ],
  )

  const formatDateToInput = useCallback((date) => {
    if (!date) return ""
    const d = new Date(date)
    return d.toISOString().split("T")[0]
  }, [])

  // Helper function to display Device ID(s)
  const displayDeviceId = useCallback((deviceId) => {
    if (!deviceId || deviceId === "0" || deviceId === 0) {
      return "No Device ID (New Visitor)"
    }

    // If API gives array of device IDs
    if (Array.isArray(deviceId)) {
      return deviceId.length > 0 ? deviceId.join(", ") : "No devices"
    }

    // If API gives string like "109,110,112"
    if (typeof deviceId === "string" && deviceId.includes(",")) {
      return deviceId
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean)
        .join(", ")
    }

    // Default: single device
    return deviceId
  }, [])

  // Helper function to display Product Information - memoized
  const displayProductInfo = useCallback(
    (complaints) => {
      if (!complaints || complaints.length === 0) {
        return "No Product Information"
      }

      const uniqueProducts = getUniqueProductNames(complaints)
      if (uniqueProducts.length === 0) {
        return "No Product Information"
      }

      return uniqueProducts
    },
    [getUniqueProductNames],
  )

  // Helper function to get current status display - memoized
  const getCurrentStatusDisplay = useCallback(() => {
    const current = currentTicketStatus || "OPN"
    switch (current) {
      case "OPN":
        return { label: "Open", color: "success" }
      case "INP":
        return { label: "In Progress", color: "warning" }
      case "CRS":
        return { label: "Resolved", color: "info" }
      case "CNR":
        return { label: "No Response", color: "danger" }
      default:
        return { label: "Unknown", color: "secondary" }
    }
  }, [currentTicketStatus])

  const customStyles = `
    .gradient-header {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    }
        
    .info-header {
      background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
    }
        
    .success-header {
      background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
    }
        
    .card-shadow {
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
      border: none;
      border-radius: 15px;
      overflow: hidden;
    }
        
    .info-row {
      transition: background-color 0.2s ease;
    }
        
    .info-row:hover {
      background-color: #f8f9fa;
    }
        
    .status-badge {
      font-size: 0.85rem;
      padding: 0.5rem 1rem;
      border-radius: 20px;
      font-weight: 500;
    }
        
    .form-control:focus, .form-select:focus {
      border-color: #667eea;
      box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
    }

    .form-control.is-invalid, .form-select.is-invalid {
      border-color: #dc3545;
      box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
    }
        
    .btn-gradient {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border: none;
      border-radius: 10px;
      font-weight: 500;
      transition: transform 0.2s ease;
    }
        
    .btn-gradient:hover {
      transform: translateY(-2px);
      box-shadow: 0 5px 15px rgba(102, 126, 234, 0.4);
    }
        
    .back-btn {
      background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
      border: none;
      border-radius: 10px;
      color: white;
      transition: transform 0.2s ease;
    }
        
    .back-btn:hover {
      transform: translateY(-2px);
      color: white;
    }

    .toast-container-custom {
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 1050;
    }

    .custom-toast {
      min-width: 350px;
      border-radius: 10px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    .toast-error {
      border-left: 4px solid #dc3545;
    }

    .toast-success {
      border-left: 4px solid #28a745;
    }

    .invalid-feedback {
      display: block;
      font-weight: 600;
      font-size: 0.875rem;
    }

    .workflow-info {
      background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
      border-left: 4px solid #2196f3;
      padding: 1rem;
      border-radius: 8px;
      margin-bottom: 1rem;
    }

    .followup-required {
      background: linear-gradient(135deg, #fff3e0 0%, #fce4ec 100%);
      border-left: 4px solid #ff9800;
      padding: 0.75rem;
      border-radius: 6px;
      margin-top: 0.5rem;
    }
        
    @media (max-width: 768px) {
      .container-fluid {
        padding-left: 15px;
        padding-right: 15px;
      }
            
      .card-body {
        padding: 1rem;
      }
            
      .table td {
        padding: 0.75rem 0.5rem;
        font-size: 0.9rem;
      }
            
      .btn-lg {
        padding: 0.75rem 1rem;
        font-size: 1rem;
      }

      .toast-container-custom {
        top: 10px;
        right: 10px;
        left: 10px;
      }

      .custom-toast {
        min-width: auto;
      }
    }
  `

  // Memoized computed values
  const allowedOptions = useMemo(() => getAllowedStatusOptions(), [getAllowedStatusOptions])
  const currentStatusDisplay = useMemo(() => getCurrentStatusDisplay(), [getCurrentStatusDisplay])
  const deviceIDs = useMemo(() => {
    if (!ticketDetail?.complaints || !Array.isArray(ticketDetail.complaints)) {
      return []
    }

    const extractedIDs = ticketDetail.complaints
      .map((complaint) => complaint.deviceID?.toString())
      .filter(Boolean)
      .filter((id) => id !== "0")

    const uniqueIDs = [...new Set(extractedIDs)]
    console.log("Extracted device IDs:", uniqueIDs)
    return uniqueIDs
  }, [ticketDetail])

  return (
    <>
      <style>{customStyles}</style>
      <div style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
        {/* Toast Notifications */}
        <div className="toast-container-custom">
          <Toast
            show={showRemarkToast}
            onClose={() => setShowRemarkToast(false)}
            className="custom-toast toast-error"
            bg="light"
          >
            <Toast.Header className="bg-danger text-white">
              <FaExclamationTriangle className="me-2" />
              <strong className="me-auto">Remark Required</strong>
            </Toast.Header>
            <Toast.Body className="text-dark fw-medium">{toastMessage}</Toast.Body>
          </Toast>

          <Toast
            show={showStatusToast}
            onClose={() => setShowStatusToast(false)}
            className="custom-toast toast-error mt-2"
            bg="light"
          >
            <Toast.Header className="bg-warning text-dark">
              <FaExclamationTriangle className="me-2" />
              <strong className="me-auto">Status Required</strong>
            </Toast.Header>
            <Toast.Body className="text-dark fw-medium">{toastMessage}</Toast.Body>
          </Toast>

          <Toast
            show={showFollowUpToast}
            onClose={() => setShowFollowUpToast(false)}
            className="custom-toast toast-error mt-2"
            bg="light"
          >
            <Toast.Header className="bg-info text-white">
              <FaCalendarAlt className="me-2" />
              <strong className="me-auto">Follow-up Required</strong>
            </Toast.Header>
            <Toast.Body className="text-dark fw-medium">{toastMessage}</Toast.Body>
          </Toast>

          <Toast
            show={showSuccessToast}
            onClose={() => setShowSuccessToast(false)}
            className="custom-toast toast-success mt-2"
            bg="light"
          >
            <Toast.Header className="bg-success text-white">
              <FaCheckCircle className="me-2" />
              <strong className="me-auto">Success</strong>
            </Toast.Header>
            <Toast.Body className="text-dark fw-medium">{toastMessage}</Toast.Body>
          </Toast>
        </div>

        <Container fluid className="px-3 px-md-4 py-4">
          {/* Header */}
          <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center mb-4">
            <Button variant="outline-primary" size="sm" onClick={() => navigate(-1)} className="mb-2 mb-sm-0 me-sm-3">
              <FaArrowLeft className="me-2" />
              <span className="d-none d-sm-inline">Back to Tickets</span>
              <span className="d-inline d-sm-none">Back</span>
            </Button>
            <div>
              <h4 className="mb-1 text-dark fw-bold">
                <FaClipboardList className="me-2 text-primary" />
                Ticket Details
              </h4>
              <p className="text-muted mb-0 small">
                Ticket ID: <span className="fw-medium text-primary">CBR-{ticketId || "unknown"}</span>
                {" • "}
                Current Status:{" "}
                <Badge bg={currentStatusDisplay.color} className="ms-1">
                  {currentStatusDisplay.label}
                </Badge>
              </p>
            </div>
          </div>

          {/* Ticket Information Card */}
          <Card className="mb-4 card-shadow">
            <Card.Header className="gradient-header text-white py-3">
              <h6 className="mb-0 fw-semibold">
                <FaInfoCircle className="me-2" />
                Ticket Information
              </h6>
            </Card.Header>
            <Card.Body className="p-0">
              {ticketDetail?.complaints &&
              Array.isArray(ticketDetail.complaints) &&
              ticketDetail.complaints.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-borderless mb-0">
                    <tbody>
                      <tr className="info-row">
                        <td className="fw-medium text-muted py-3 px-4" style={{ width: "180px" }}>
                          <FaUser className="me-2 text-primary" />
                          Complainant
                        </td>
                        <td className="py-3 px-4">
                          <div className="fw-medium text-dark">{ticketDetail?.complaintname || "Unknown User"}</div>
                        </td>
                      </tr>

                      {/* Products */}
                      <tr className="border-top info-row">
                        <td className="fw-medium text-muted py-3 px-4">
                          <FaTools className="me-2 text-success" />
                          Products
                        </td>
                        <td className="py-3 px-4">
                          {(() => {
                            const productInfo = [...new Set(ticketDetail.complaints.map((c) => c.product_name))]

                            if (productInfo.length === 0) {
                              return (
                                <Badge bg="secondary" className="status-badge">
                                  <FaCog className="me-1" />
                                  No Product Information
                                </Badge>
                              )
                            }

                            return (
                              <div className="d-flex flex-wrap gap-1">
                                {productInfo.map((productName, idx) => (
                                  <Badge key={idx} bg="primary" className="status-badge">
                                    <FaTools className="me-1" />
                                    {productName}
                                  </Badge>
                                ))}
                              </div>
                            )
                          })()}
                        </td>
                      </tr>

                      {/* Contact */}
                      <tr className="border-top info-row">
                        <td className="fw-medium text-muted py-3 px-4">
                          <FaPhone className="me-2 text-info" />
                          Contact
                        </td>
                        <td className="py-3 px-4">
                          <span className="fw-medium text-dark">{ticketDetail.complaint_mobile || "Not provided"}</span>
                        </td>
                      </tr>

                      {/* Staff Remark */}
                      <tr className="border-top info-row">
                        <td className="fw-medium text-muted py-3 px-4">
                          <FaComment className="me-2 text-warning" />
                          Last Remark
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-dark">
                            {ticketDetail.Description ||
                              ticketDetail.description ||
                              "No additional information provided"}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-4 text-center">
                  {loading ? (
                    <div>
                      <Skeleton height={40} className="mb-2" />
                      <Skeleton height={40} className="mb-2" />
                      <Skeleton height={40} />
                    </div>
                  ) : (
                    <div className="text-muted">
                      <FaExclamationTriangle className="me-2" />
                      No ticket data available
                    </div>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>

          <Row className="g-4">
            {/* Issue Details */}
            <Col lg={6}>
              <Card className="h-100 card-shadow">
                <Card.Header className="text-dark py-3">
                  <h6 className="mb-0 fw-semibold">
                    <FaComment className="me-2" />
                    Issue Details
                  </h6>
                </Card.Header>
                <Card.Body className="p-4">
                  <div className="mb-4">
                    <h6 className="text-muted mb-3 fw-medium">
                      <FaComment className="me-2" />
                      Ticket Remarks:
                    </h6>
                    {ticketRemarks && ticketRemarks.length > 0 ? (
                      <div className="space-y-3">
                        {ticketRemarks.map((remarkItem, index) => (
                          <div key={index} className="bg-light p-3 rounded-3 mb-3">
                            <p className="mb-2 text-dark">{remarkItem.remark}</p>
                            {remarkItem.createdAt && (
                              <small className="text-muted">
                                <FaCalendarAlt className="me-1" />
                                {new Date(remarkItem.createdAt).toLocaleDateString()}
                              </small>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="bg-light p-3 rounded-3">
                        <span className="text-muted">
                          <FaInfoCircle className="me-2" />
                          No remarks found for this ticket
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="mb-4">
                    <h6 className="text-muted mb-3 fw-medium">
                      <FaExclamationTriangle className="me-2" />
                      Issue Regarding:
                    </h6>
                    {ticketDetail?.complaints && ticketDetail.complaints.length > 0 ? (
                      <div className="d-flex flex-wrap gap-2">
                        {deduplicateComplaints(ticketDetail.complaints).map((complaint, index) => (
                          <Badge key={`${complaint.complaintID}-${index}`} bg="danger" className="status-badge">
                            <FaExclamationTriangle className="me-1" />
                            {complaint.complaint_detail}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <div className="bg-light p-3 rounded-3">
                        <span className="text-muted">
                          <FaInfoCircle className="me-2" />
                          No specific complaints found
                        </span>
                      </div>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Update Form */}
            <Col lg={6}>
              <Card className="h-100 card-shadow">
                <Card.Header className="text-dark py-3">
                  <h6 className="mb-0 fw-semibold">
                    <FaClipboardList className="me-2" />
                    Update Ticket
                  </h6>
                </Card.Header>
                <Card.Body className="p-4">
                  {isOriginallyClosed() && (
                    <Alert variant="warning" className="mb-4 border-0 rounded-3">
                      <FaExclamationTriangle className="me-2" />
                      <strong>Notice:</strong> This ticket was already closed, updates are disabled.
                    </Alert>
                  )}

                  {/* Workflow Information */}
                  {allowedOptions.length > 0 && !isCurrentTicketClosed() && (
                    <div className="workflow-info">
                      <h6 className="mb-2 text-primary">
                        <FaInfoCircle className="me-2" />
                        Status Workflow
                      </h6>
                      <p className="mb-0 small">
                        Current Status: <strong>{currentStatusDisplay.label}</strong>
                        <br />
                        Available status options: {allowedOptions.map((opt) => opt.label).join(", ")}
                      </p>
                    </div>
                  )}

                  {/* Show closed ticket message */}
                  {isCurrentTicketClosed() && (
                    <Alert variant="info" className="mb-4 border-0 rounded-3">
                      <FaInfoCircle className="me-2" />
                      <strong>Ticket is closed so not able to change.</strong>
                    </Alert>
                  )}

                  <Form onSubmit={handleSubmit}>
                    <div className="mb-4">
                      <Form.Label className="fw-semibold text-dark mb-2">
                        <FaComment className="me-2 text-primary" />
                        Remark <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        value={agentRemarks}
                        onChange={(e) => {
                          const input = e.target.value
                          const capitalized = input.charAt(0).toUpperCase() + input.slice(1)
                          setAgentRemarks(capitalized)
                          // Clear error when user starts typing
                          if (remarkError) {
                            setRemarkError("")
                            setShowRemarkToast(false)
                          }
                        }}
                        placeholder="Enter your detailed remark here..."
                        disabled={allFieldsDisabled || isCurrentTicketClosed()}
                        isInvalid={!!remarkError}
                        className="rounded-3"
                        style={{ resize: "vertical" }}
                      />
                      {remarkError && (
                        <div className="invalid-feedback">
                          <FaExclamationTriangle className="me-1" />
                          {remarkError}
                        </div>
                      )}
                    </div>
                    <div className="mb-4">
                      <Form.Label className="fw-semibold text-dark mb-2">
                        <FaClipboardList className="me-2 text-success" />
                        Status <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Select
                        value={status}
                        onChange={(e) => handleStatusChange(e.target.value)}
                        disabled={
                          allFieldsDisabled ||
                          isFollowUpLocked() ||
                          allowedOptions.length === 0 ||
                          isCurrentTicketClosed()
                        }
                        isInvalid={!!statusError}
                        className="rounded-3"
                      >
                        <option value="">Select status</option>
                        {allowedOptions.map((opt) => (
                          <option key={opt.code} value={opt.code}>
                            {opt.label}
                          </option>
                        ))}
                      </Form.Select>
                      {statusError && (
                        <div className="invalid-feedback">
                          <FaExclamationTriangle className="me-1" />
                          {statusError}
                        </div>
                      )}
                      {allowedOptions.length === 0 && !isCurrentTicketClosed() && (
                        <Form.Text className="text-info">
                          <FaInfoCircle className="me-1" />
                          This ticket is already in its final status. No further status changes are allowed.
                        </Form.Text>
                      )}
                      {currentTicketStatus === "INP" && (
                        <Form.Text className="text-info">
                          <FaInfoCircle className="me-1" />
                          You can keep it In Progress to update follow-up date, or close the ticket.
                        </Form.Text>
                      )}
                      {isFollowUpLocked() && (
                        <Form.Text className="text-warning">
                          <FaExclamationTriangle className="me-1" />
                          Status change is locked until the follow-up date.
                        </Form.Text>
                      )}
                    </div>
                    <div className="mb-4">
                      <Form.Label className="fw-semibold text-dark mb-2">
                        <FaCalendarAlt className="me-2 text-info" />
                        Follow-Up Date {isFollowUpRequired() && <span className="text-danger">*</span>}
                      </Form.Label>
                      <Form.Control
                        type="date"
                        value={formatDateToInput(followUpDate)}
                        onChange={(e) => {
                          setFollowUpDate(e.target.value)
                          // Clear error when user selects date
                          if (followUpError) {
                            setFollowUpError("")
                            setShowFollowUpToast(false)
                          }
                        }}
                        disabled={
                          allFieldsDisabled || isFollowUpLocked() || isFollowUpDisabled() || isCurrentTicketClosed()
                        }
                        isInvalid={!!followUpError}
                        className="rounded-3"
                        min={(() => {
                          const d = new Date()
                          const y = d.getFullYear()
                          const m = String(d.getMonth() + 1).padStart(2, "0")
                          const day = String(d.getDate()).padStart(2, "0")
                          return `${y}-${m}-${day}`
                        })()}
                      />
                      {followUpError && (
                        <div className="invalid-feedback">
                          <FaExclamationTriangle className="me-1" />
                          {followUpError}
                        </div>
                      )}
                      {isFollowUpRequired() && !isCurrentTicketClosed() && (
                        <div className="followup-required">
                          <small className="text-warning fw-medium">
                            <FaCalendarAlt className="me-1" />
                            Follow-up date is mandatory for "In Progress" status
                          </small>
                        </div>
                      )}
                      {isFollowUpDisabled() && !isCurrentTicketClosed() && (
                        <Form.Text className="text-muted">
                          <FaInfoCircle className="me-1" />
                          Follow-up date is not applicable for closed tickets
                        </Form.Text>
                      )}
                    </div>
                    <div className="d-grid">
                      <Button
                        type="submit"
                        size="lg"
                        disabled={
                          allFieldsDisabled || loading || allowedOptions.length === 0 || isCurrentTicketClosed()
                        }
                        className="btn-gradient text-white fw-semibold py-3"
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Updating Ticket...
                          </>
                        ) : (
                          <>
                            <FaClipboardList className="me-2" />
                            Update Ticket Status
                          </>
                        )}
                      </Button>
                    </div>
                    {error && (
                      <Alert variant="danger" className="mt-4 mb-0 border-0 rounded-3">
                        <FaExclamationTriangle className="me-2" />
                        <strong>Error:</strong> {error}
                      </Alert>
                    )}
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  )
}

export default UserConversation
